import { YellowButton } from '@monorepo/shared-lib';
import { List, Typography } from 'antd';
import { ReactComponent as CheckIcon } from 'assets/images/icons/checkIcon.svg';
import { ReactComponent as IconClose } from 'assets/images/icons/iconClose.svg';
import UpgradeImage from 'assets/images/upgrade-pic.svg';
import { StyledCard } from 'components/Custom/card';
import {
  BlackSpan,
  CardDescBoldGraySpan,
  CardDescBoldSpan,
  CardDescGreenSpan,
} from 'components/Custom/spans';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { AccountType } from 'store/api/enums';
import { LicenseType } from 'store/app/enums';
import { User, UserLicence } from 'store/auth/models';
import { Plan } from 'store/billing/models';
import { CurrentSubscription } from 'store/billing/models/currentSubscription';
import { getNextPlanPrice } from 'utils/getNextPlanPrice';
import { billingPageLink, pricingPageLink } from '.';

interface UpgradeCardProps {
  showCard: boolean;
  setShowCard: (show: boolean) => void;
  selectedAccount?: User;
  redirectPage: (url: string) => void;
  currentSubscription?: CurrentSubscription;
  getFeaturesOfPlans?: () => Plan[];
  userLicence?: UserLicence;
}

export const UpgradeCard: React.FC<UpgradeCardProps> = ({
  showCard,
  setShowCard,
  selectedAccount,
  redirectPage,
  currentSubscription,
  getFeaturesOfPlans,
  userLicence,
}) => {
  const { t } = useTranslation();

  if (
    !showCard ||
    !selectedAccount?.accountType ||
    AccountType[selectedAccount?.accountType] !== AccountType.OWNER
  ) {
    return null;
  }

  return (
    <StyledCard
      className="profile-upgrade-banner"
      width="100%"
      $nopadding
      $removeHeader
      extra={
        <button
          type="button"
          className="profile-upgrade-banner-close"
          aria-label={t('common.close')}
          onClick={() => setShowCard(false)}
        >
          <IconClose
            aria-hidden
            focusable="false"
            className="profile-upgrade-banner-close-icon"
          />
        </button>
      }
    >
      <div className="profile-upgrade-banner-inner">
        <div className="profile-upgrade-banner-copy">
          <Typography
            style={{
              fontSize: '24px',
              fontWeight: '600',
              lineHeight: '26px',
              textAlign: 'left',
              color: 'var(--color-text-dark, #042a2a)',
              marginBottom: '10px',
            }}
          >
            <CardDescGreenSpan>{t('other.unlock')}</CardDescGreenSpan>{' '}
            {t('other.all-features')}
            <br />
            {t('other.simplify-vat-tagline')} <br />
            {(() => {
              // Safety check for required props
              if (!currentSubscription || !getFeaturesOfPlans) {
                return (
                  <CardDescBoldGraySpan>
                    {t('billing.contact-us-enterprise-pricing')}
                  </CardDescBoldGraySpan>
                );
              }

              const nextPlanInfo = getNextPlanPrice(
                currentSubscription,
                getFeaturesOfPlans
              );

              if (
                nextPlanInfo.price !== null &&
                nextPlanInfo.currency !== null
              ) {
                // Ensure both price and currency are strings and not objects
                const priceStr =
                  typeof nextPlanInfo.price === 'object'
                    ? t('billing.contact-us')
                    : String(nextPlanInfo.price);
                const currencyStr =
                  typeof nextPlanInfo.currency === 'object'
                    ? 'EUR'
                    : String(nextPlanInfo.currency);

                // Additional safety check - if price is still an object, show fallback
                if (typeof nextPlanInfo.price === 'object') {
                  console.error(
                    'Price is still an object after extraction:',
                    nextPlanInfo.price
                  );
                  return (
                    <CardDescBoldGraySpan>
                      {t('billing.contact-us-enterprise-pricing')}
                    </CardDescBoldGraySpan>
                  );
                }

                return (
                  <CardDescBoldSpan>
                    {t('billing.from')}{' '}
                    <CardDescBoldSpan fontSize="16px" fontWeight="700">
                      {priceStr} {currencyStr}{' '}
                    </CardDescBoldSpan>
                    <CardDescBoldGraySpan>{t('billing.per-month')}</CardDescBoldGraySpan>
                  </CardDescBoldSpan>
                );
              } else {
                return (
                  <CardDescBoldGraySpan>
                    {t('billing.contact-us-enterprise-pricing')}
                  </CardDescBoldGraySpan>
                );
              }
            })()}
          </Typography>
          <List
            style={{ marginBottom: '15px' }}
            dataSource={[
              t('billing.more-vat-functionality'),
              t('billing.connect-more-entities'),
              t('billing.integrations-xero-excel-more'),
            ]}
            split={false}
            size="small"
            renderItem={(item) => (
              <List.Item style={{ padding: '0px 3px' }}>
                <CheckIcon style={{ width: '15px', height: '15px' }} />{' '}
                <BlackSpan>{item}</BlackSpan>
              </List.Item>
            )}
          />
          <YellowButton
            type="primary" //@ts-ignore
            width="139px"
            height="32"
            fontSize="13px"
            className="yellow-btn"
            $textColor="var(--color-text-dark, #042a2a)"
            onClick={() => {
              if (userLicence?.licenseType === LicenseType.TRIAL) {
                redirectPage(pricingPageLink);
              } else {
                redirectPage(billingPageLink);
              }
            }}
          >
            {t('billing.upgrade-now')}
          </YellowButton>
        </div>
        <img
          alt=""
          className="profile-upgrade-banner-art"
          src={UpgradeImage}
        />
      </div>
    </StyledCard>
  );
};
